import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Col, Flex, Typography, message } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { CATEGORY, LANGUAGE_TYPE } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useResolveClient } from "../../hooks/useResolveClient";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { updateClient, validateClientName } from "../../service/client.service";
import { cacheClients, resetClientForm, setClientForm } from "../../slices/clientSlice";
import ClientForm from "../CreateClient/ClientForm.component";
import styles from "./createnotemodal.module.scss";

const CATEGORY_LABELS: Record<number, string> = {
  [CATEGORY.INDIVIDUAL]: "Individual",
  [CATEGORY.RELATIONSHIP]: "Relationship",
  [CATEGORY.FAMILY]: "Family",
  [CATEGORY.GROUP]: "Group",
};

const LANGUAGE_LABELS: Record<number, string> = {
  [LANGUAGE_TYPE.ENGLISH]: "English",
  [LANGUAGE_TYPE.SPANISH]: "Spanish",
  [LANGUAGE_TYPE.KOREAN]: "Korean",
  [LANGUAGE_TYPE.MANDARIN]: "Mandarin",
  [LANGUAGE_TYPE.HINDI]: "Hindi",
  [LANGUAGE_TYPE.ARABIC]: "Arabic",
  [LANGUAGE_TYPE.PORTUGUESE]: "Portuguese",
  [LANGUAGE_TYPE.PUNJABI]: "Punjabi",
  [LANGUAGE_TYPE.FRENCH]: "French",
};

const metaTextStyle = { fontSize: 13, color: "var(--ant-color-text-tertiary, #8c8c8c)" };

/**
 * Read-mostly patient snapshot shown in the CHOOSE PATIENT section of the start-encounter
 * drawer, so a provider can catch a wrong stored language (STT reads Client.language_type
 * directly, not the note payload) before recording rather than after. The edit icon swaps
 * this card for the same ClientForm used to add a patient, pre-filled for editing. Name is
 * left out — the ClientSelector dropdown right above already shows it.
 */
const EncounterPatientBar = () => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const { referIndividualBy } = useSpecialityReference();
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { clientForm } = useAppSelector((state) => state.client);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const { client } = useResolveClient({
    id: recordingDetail?.client_id,
    uuid: recordingDetail?.client_uuid,
    name: recordingDetail?.client_name,
  });

  if (!client) return null;

  const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";

  const metaDetails: string[] = [];
  if (formattedDob) metaDetails.push(`DOB: ${formattedDob}`);
  if (client.mrn) metaDetails.push(`MRN/Patient ID: ${client.mrn}`);
  metaDetails.push(`Category: ${CATEGORY_LABELS[client.category_type] ?? CATEGORY_LABELS[CATEGORY.INDIVIDUAL]}`);
  metaDetails.push(`Language: ${LANGUAGE_LABELS[client.language_type] ?? LANGUAGE_LABELS[LANGUAGE_TYPE.ENGLISH]}`);

  const openEdit = () => {
    dispatch(setClientForm(client));
    setIsEditing(true);
  };

  const closeEdit = () => {
    dispatch(resetClientForm());
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (client.id == null) return;
    const validationError = validateClientName(clientForm, referIndividualBy);
    if (validationError) {
      message.warning(validationError);
      return;
    }
    setSaving(true);
    try {
      const response = await updateClient(client.id, {
        ...clientForm,
        name: clientForm.name.trim(),
        diagnosis_ids: clientForm.diagnosis_ids,
      });
      dispatch(cacheClients([response.data]));
      dispatch(resetClientForm());
      setIsEditing(false);
      message.success(`${referIndividualBy} updated successfully`);
    } catch (error) {
      console.error(error);
      message.error(`Failed to update ${referIndividualBy.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
        <Card title={`Edit ${referIndividualBy}`} id="special-card" extra={<CloseOutlined onClick={closeEdit} />}>
          <ClientForm />
          <Flex justify="end" style={{ marginTop: 12 }}>
            <Button type="primary" loading={saving} disabled={saving} onClick={handleSave}>
              Save Changes
            </Button>
          </Flex>
        </Card>
      </Col>
    );
  }

  return (
    <Col span={24} className={styles[`${componentName}__modal-row`]}>
      <Flex align="center" justify="space-between" gap={8} style={{ marginBottom: 4 }}>
        <Typography.Text style={metaTextStyle}>{metaDetails.join(" · ")}</Typography.Text>
        <Flex
          align="center"
          gap={4}
          role="button"
          tabIndex={0}
          onClick={openEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openEdit();
          }}
          style={{ cursor: "pointer", color: "var(--primary)", flexShrink: 0 }}
        >
          <EditOutlined />
          <Typography.Text style={{ fontSize: 13, color: "var(--primary)" }}>Edit</Typography.Text>
        </Flex>
      </Flex>
    </Col>
  );
};

export default EncounterPatientBar;
